import { useEffect, useMemo, useState } from "react";
import { formatLocalDate } from "../../../utils/formatDate";
import TelemetriaService from "../../../services/resource/telemetriaService";
import { useSearchParams } from "react-router-dom";

const META = 98;

const indiceStyle = (value) =>
    value >= META
        ? "font-bold text-teal-600"
        : "font-bold text-red-500";

const indiceBg = (value) =>
    value >= META
        ? "bg-teal-50 border-teal-200"
        : "bg-red-50 border-red-200";

function TabelaTelemetriaPhone() {
    const [searchParams] = useSearchParams();

    const ano = Number(searchParams.get("ano"));
    const mes = Number(searchParams.get("mes"));
    const funcionarioId = searchParams.get("funcionarioId");

    const telemetriaService = new TelemetriaService();

    const [telemetrias, setTelemetrias] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        telemetriaService
            .listar({ funcionarioId: funcionarioId, ano, mes })
            .then((res) => setTelemetrias(res.data ?? []))
            .finally(() => setLoading(false));
    }, []);

    const medias = useMemo(() => {
        if (!telemetrias.length) return { segurancaViaria: 0, manutencao: 0, performance: 0 };
        const soma = telemetrias.reduce(
            (acc, t) => ({
                segurancaViaria: acc.segurancaViaria + (t.segurancaViaria || 0),
                manutencao: acc.manutencao + (t.manutencao || 0),
                performance: acc.performance + (t.performance || 0),
            }),
            { segurancaViaria: 0, manutencao: 0, performance: 0 }
        );
        const n = telemetrias.length;
        return {
            segurancaViaria: (soma.segurancaViaria / n).toFixed(2),
            manutencao: (soma.manutencao / n).toFixed(2),
            performance: (soma.performance / n).toFixed(2),
        };
    }, [telemetrias]);

    return (
        <div className="min-h-screen flex flex-col gap-3 p-3">

            {/* ── Cards de médias ── */}
            {!loading && telemetrias.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                    {[
                        { label: "Seg. Viária", value: medias.segurancaViaria, icon: "fa-shield" },
                        { label: "Manutenção", value: medias.manutencao, icon: "fa-wrench" },
                        { label: "Performance", value: medias.performance, icon: "fa-tachometer" },
                    ].map(({ label, value, icon }) => (
                        <div
                            key={label}
                            className={`rounded-xl border px-3 py-2.5 text-center ${indiceBg(Number(value))}`}
                        >
                            <i className={`fa ${icon} text-sm mb-1 ${indiceStyle(Number(value))}`} />
                            <p className="text-[9px] text-gray-400 uppercase mb-0.5">{label}</p>
                            <p className={`text-base leading-tight ${indiceStyle(Number(value))}`}>
                                {value}
                            </p>
                            <p className="text-[9px] text-gray-400">média</p>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Loading ── */}
            {loading && (
                <div className="flex flex-col items-center justify-center py-14 gap-2 text-gray-400">
                    <i className="fa fa-spinner fa-spin text-2xl text-teal-500" />
                    <p className="text-xs">Carregando telemetrias...</p>
                </div>
            )}

            {/* ── Empty ── */}
            {!loading && telemetrias.length === 0 && (
                <div className="flex flex-col items-center justify-center py-14 gap-2 text-gray-400">
                    <i className="fa fa-tachometer text-4xl opacity-20" />
                    <p className="text-xs">Nenhuma telemetria encontrada no período.</p>
                </div>
            )}

            {/* ── Lista de registros ── */}
            {!loading && telemetrias.length > 0 && (
                <>
                    <div className="space-y-2">
                        {telemetrias.map((t, i) => (
                            <TelemetriaCard key={t.id ?? i} row={t} />
                        ))}
                    </div>

                    {/* Rodapé */}
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 flex justify-between items-center">
                        <span className="text-xs text-gray-500">
                            {telemetrias.length} registro{telemetrias.length !== 1 ? "s" : ""}
                        </span>
                        <span className="text-xs text-gray-400">
                            Meta: <span className="font-semibold text-teal-600">{META}</span>
                        </span>
                    </div>
                </>
            )}
        </div>
    );
}

export default TabelaTelemetriaPhone;

// ─── Card individual ──────────────────────────────────────────────────────────
function TelemetriaCard({ row }) {
    const indices = [
        { label: "Seg. Viária", value: row.segurancaViaria, icon: "fa-shield" },
        { label: "Manutenção", value: row.manutencao, icon: "fa-wrench" },
        { label: "Performance", value: row.performance, icon: "fa-tachometer" },
    ];

    // O card fica vermelho se qualquer índice for abaixo da meta
    const temAlerta = indices.some((idx) => (idx.value ?? 0) < META);

    return (
        <div className={`bg-white rounded-xl border overflow-hidden ${temAlerta ? "border-red-200" : "border-gray-200"}`}>
            {/* Cabeçalho */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                <div>
                    <p className="text-sm font-semibold text-gray-800 leading-tight">
                        {row.funcionarioNome ?? "—"}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{row.filialNome ?? "—"}</p>
                </div>
                <div className="text-right">
                    <p className="text-[10px] text-gray-400">Data</p>
                    <p className="text-xs font-medium text-gray-700">{formatLocalDate(row.data) ?? "—"}</p>
                </div>
            </div>

            {/* Índices */}
            <div className="grid grid-cols-3 divide-x divide-gray-100 px-0">
                {indices.map(({ label, value, icon }) => (
                    <div key={label} className="flex flex-col items-center py-3 px-2">
                        <i className={`fa ${icon} text-sm mb-1 ${indiceStyle(value ?? 0)}`} />
                        <p className="text-[9px] text-gray-400 uppercase mb-0.5">{label}</p>
                        <p className={`text-base leading-tight ${indiceStyle(value ?? 0)}`}>
                            {value ?? "—"}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
}
